import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConversationScripts } from "@/hooks/useConversationScripts";
import type { ConversationScript } from "@/lib/api";

// One logical script = one useCase (DB enforces @@unique([useCase, version])).
// The list collapses all versions of a useCase into a single row; the version
// column shows the latest (highest) version and the row links to it.
type ScriptGroup = {
  useCase: string;
  current: ConversationScript;
  currentVersion: number;
};

function groupByUseCase(scripts: ConversationScript[]): ScriptGroup[] {
  const byUseCase = new Map<string, ConversationScript>();
  for (const s of scripts) {
    const existing = byUseCase.get(s.useCase);
    if (!existing || s.version > existing.version) byUseCase.set(s.useCase, s);
  }
  return [...byUseCase.values()]
    .map((current) => ({ useCase: current.useCase, current, currentVersion: current.version }))
    .sort((a, b) => a.current.name.localeCompare(b.current.name));
}

export function ScriptsList(): React.JSX.Element {
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>("");
  const navigate = useNavigate();

  const { data: scripts, isPending, isError } = useConversationScripts({
    agentType: agentTypeFilter || undefined,
  });

  if (isError) return <div className="p-6 text-sm text-red-500">Error al cargar scripts.</div>;

  const groups = scripts ? groupByUseCase(scripts) : [];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-900">Scripts de conversación</h1>
      </div>

      <div className="flex gap-3">
        <select
          value={agentTypeFilter}
          onChange={(e) => setAgentTypeFilter(e.target.value)}
          className="rounded border border-ink-200 px-2 py-1 text-sm text-ink-700"
        >
          <option value="">Todos los tipos</option>
          <option value="admissions">Admisiones</option>
          <option value="internal_faq">FAQ interno</option>
          <option value="store">Tienda</option>
        </select>
      </div>

      {isPending ? (
        <div className="text-sm text-ink-500">Cargando…</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-050 text-xs font-semibold uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Caso de uso</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Versión actual</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {groups.map((g) => (
                <tr
                  key={g.useCase}
                  className="cursor-pointer hover:bg-ink-050"
                  onClick={() => void navigate(`/super-admin/scripts/${g.current.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-ink-900">{g.current.name}</td>
                  <td className="px-4 py-3 text-ink-500">{g.useCase}</td>
                  <td className="px-4 py-3 text-ink-500">{g.current.agentType}</td>
                  <td className="px-4 py-3 text-ink-500">v{g.currentVersion}</td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-ink-500">
                    No hay scripts. Se versionan en el repositorio y se sincronizan al desplegar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
